import React, { useState } from 'react';
import { Select, Button, Alert, Spin, Tooltip, Modal, Tag } from 'antd';
import { HistoryOutlined, EyeOutlined, RollbackOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useGetConfigVersions, useGetConfigByVersion, usePutConfigLive } from '../../hooks/productConfiguration';
import { IChatBotLiveConfig } from '../../services/chatbotConfigGenerator';
import toast from 'react-hot-toast';

interface VersionHistoryPanelProps {
  onPreviewVersion: (config: IChatBotLiveConfig, versionLabel: string) => void;
  onClearPreview: () => void;
  previewVersionLabel: string | null;
  previewVersionConfig: IChatBotLiveConfig | null;
  /** Live version number e.g. "3.1.73" from backendConfig.Version */
  activeVersion?: string;
  /** Config name of the live version e.g. "EcoSlide Configuration" */
  activeConfigName?: string;
}

/**
 * Parses a version label like "v3.1.78_20260303" into the API query param "3.1.78"
 */
const parseVersionParam = (versionLabel: string): string => {
  return versionLabel.replace(/^v/, '').replace(/_\d+$/, '');
};

/**
 * Formats a version label for display: "v3.1.78_20260303" → "v3.1.78 (2026-03-03)"
 */
const formatVersionLabel = (versionLabel: string): string => {
  const match = versionLabel.match(/^(v[\d.]+)_(\d{4})(\d{2})(\d{2})$/);
  if (match) {
    return `${match[1]} (${match[2]}-${match[3]}-${match[4]})`;
  }
  return versionLabel;
};

export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  onPreviewVersion,
  onClearPreview,
  previewVersionLabel,
  previewVersionConfig,
  activeVersion,
  activeConfigName,
}) => {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: versionsData, isLoading: versionsLoading } = useGetConfigVersions();
  const { mutate: fetchVersionConfig, isPending: isFetchingVersion } = useGetConfigByVersion();
  const { mutate: putConfigLive, isPending: isReverting } = usePutConfigLive();

  const handlePreview = () => {
    if (!selectedVersion) {
      toast.error('Please select a version to preview');
      return;
    }
    const versionParam = parseVersionParam(selectedVersion);
    fetchVersionConfig(versionParam, {
      onSuccess: (config) => {
        onPreviewVersion(config, selectedVersion);
        toast.success(`Previewing version ${formatVersionLabel(selectedVersion)}`);
      },
    });
  };

  const doRevert = (config: IChatBotLiveConfig, versionLabel: string) => {
    putConfigLive(config, {
      onSuccess: () => {
        toast.success(`Reverted to ${formatVersionLabel(versionLabel)} successfully`);
        onClearPreview();
        setSelectedVersion(null);
      },
    });
  };

  const handleRevert = () => {
    if (!selectedVersion) {
      toast.error('Please select a version to revert to');
      return;
    }

    const confirmAndRevert = (config: IChatBotLiveConfig) => {
      Modal.confirm({
        title: 'Revert to this version?',
        icon: <ExclamationCircleOutlined />,
        content: (
          <span>
            This will set <strong>{formatVersionLabel(selectedVersion)}</strong> as the live
            configuration. This action will overwrite the current live config.
          </span>
        ),
        okText: 'Yes, Revert',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => doRevert(config, selectedVersion),
      });
    };

    // If user already previewed this exact version, reuse the loaded config
    if (previewVersionConfig && previewVersionLabel === selectedVersion) {
      confirmAndRevert(previewVersionConfig);
    } else {
      // Fetch fresh before reverting
      const versionParam = parseVersionParam(selectedVersion);
      fetchVersionConfig(versionParam, {
        onSuccess: (config) => confirmAndRevert(config),
      });
    }
  };

  const isActiveVersion = (versionLabel: string) =>
    !!activeVersion && parseVersionParam(versionLabel) === activeVersion;

  const getConfigName = (versionLabel: string): string | undefined => {
    if (isActiveVersion(versionLabel)) return activeConfigName;
    if (previewVersionLabel === versionLabel) {
      const cfg = previewVersionConfig?.Config;
      return (typeof cfg === 'string' ? undefined : (cfg as any)?.configName) || undefined;
    }
    return undefined;
  };

  const versionOptions = (() => {
    const versions = versionsData?.versions || [];
    const active = versions.filter(isActiveVersion);
    const rest = versions.filter((v) => !isActiveVersion(v));
    return [...active, ...rest].map((v) => ({
      value: v,
      label: formatVersionLabel(v),
      isActive: isActiveVersion(v),
      configName: getConfigName(v),
    }));
  })();

  return (
    <div className="mb-3">
      {/* Toggle button */}
      <Button
        icon={<HistoryOutlined />}
        size="small"
        onClick={() => setIsExpanded((prev) => !prev)}
        type={isExpanded ? 'primary' : 'default'}
      >
        Version History
        {versionsData?.count != null && (
          <span className="ml-1 text-xs opacity-70">({versionsData.count})</span>
        )}
      </Button>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex flex-wrap items-center gap-2">
            {versionsLoading ? (
              <Spin size="small" />
            ) : (
              <Select
                placeholder="Select a version to preview or revert"
                style={{ minWidth: 290 }}
                value={selectedVersion}
                onChange={setSelectedVersion}
                options={versionOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
                optionRender={(option) => {
                  const data = option.data as any;
                  return (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-col min-w-0">
                        <span>{option.label}</span>
                        {data.configName && (
                          <span className="text-xs text-gray-400 truncate">{data.configName}</span>
                        )}
                      </div>
                      {data.isActive && (
                        <Tag color="green" className="ml-auto shrink-0">Active</Tag>
                      )}
                    </div>
                  );
                }}
                size="small"
              />
            )}

            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              loading={isFetchingVersion && !isReverting}
              disabled={!selectedVersion || versionsLoading}
              onClick={handlePreview}
            >
              Preview
            </Button>

            <Tooltip title={!selectedVersion ? 'Select a version first' : 'Revert live config to this version'}>
              <Button
                icon={<RollbackOutlined />}
                size="small"
                danger
                disabled={!selectedVersion || versionsLoading}
                loading={isReverting}
                onClick={handleRevert}
              >
                Revert to Version
              </Button>
            </Tooltip>

            {previewVersionLabel && (
              <Button
                icon={<CloseCircleOutlined />}
                size="small"
                onClick={() => {
                  onClearPreview();
                  setIsExpanded(false);
                }}
              >
                Clear Preview
              </Button>
            )}
          </div>

          {previewVersionLabel && (
            <Alert
              className="mt-2"
              type="warning"
              showIcon
              message={
                <span>
                  Previewing{' '}
                  <strong>{getConfigName(previewVersionLabel) || formatVersionLabel(previewVersionLabel)}</strong>
                  {' '}— switch to the <strong>Configuration View</strong> tab to see the chatbot preview.
                </span>
              }
            />
          )}
        </div>
      )}
    </div>
  );
};
